import React, { useState } from "react";
import "../LoginPage/LoginPage.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import base_url from "../../api";

const RegisterPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");

  const navigate = useNavigate();

  // Validation functions
  const validateName = (name) => {
    if (!name.trim()) {
      return "Name is required";
    }
    if (name.trim().length < 2) {
      return "Name must be at least 2 characters";
    }
    if (name.trim().length > 50) {
      return "Name must be less than 50 characters";
    }
    if (!/^[a-zA-Z\s'-]+$/.test(name.trim())) {
      return "Name can only contain letters, spaces, hyphens, and apostrophes";
    }
    return "";
  };

  const validateEmail = (email) => {
    if (!email.trim()) {
      return "Email is required";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      return "Please enter a valid email address";
    }
    return "";
  };

  const validatePhone = (phone) => {
    if (!phone.trim()) {
      return "Phone number is required";
    }
    // Extract only digits for validation
    const digitsOnly = phone.replace(/\D/g, "");
    
    // Check digit count first
    if (digitsOnly.length < 10 || digitsOnly.length > 15) {
      return "Phone number must be between 10 and 15 digits";
    }
    
    // Allow digits, spaces, hyphens, parentheses, and + for international format
    const phoneRegex = /^[\d\s\-\+\(\)]+$/;
    if (!phoneRegex.test(phone.trim())) {
      return "Please enter a valid phone number";
    }
    
    return "";
  };

  const validatePassword = (password) => {
    if (!password) {
      return "Password is required";
    }
    if (password.length < 6) {
      return "Password must be at least 6 characters";
    }
    if (password.length > 50) {
      return "Password must be less than 50 characters";
    }
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields
    const newErrors = {};
    const nameError = validateName(name);
    const emailError = validateEmail(email);
    const phoneError = validatePhone(phone);
    const passwordError = validatePassword(password);

    if (nameError) newErrors.name = nameError;
    if (emailError) newErrors.email = emailError;
    if (phoneError) newErrors.phone = phoneError;
    if (passwordError) newErrors.password = passwordError;

    if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fix the errors in the form", { position: "top-right" });
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const response = await fetch(`${base_url}/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, phone, password }),
      });

      const data = await response.json();
      console.log("Register API:", data);

      if (response.ok) {
        // Show verification modal
        setRegisteredEmail(email);
        setShowVerificationModal(true);
        
        toast.success(data.message || "Registration successful! Please check your email.", {
          position: "top-right",
          autoClose: 5000,
        });
      } else {
        toast.error(data.message || "Signup failed. Try again.", {
          position: "top-right",
        });
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again later.", {
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* Register Section */}
      <div className="login-left">
        <h2>Create Account</h2>
        <form className="login-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="register-name">Full Name <span className="required-asterisk">*</span></label>
            <input
              id="register-name"
              type="text"
              placeholder="Enter your full name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) {
                  setErrors({ ...errors, name: validateName(e.target.value) });
                }
              }}
              onBlur={() => setErrors({ ...errors, name: validateName(name) })}
              className={errors.name ? "error-input" : ""}
              required
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="register-email">Email Address <span className="required-asterisk">*</span></label>
            <input
              id="register-email"
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) {
                  setErrors({ ...errors, email: validateEmail(e.target.value) });
                }
              }}
              onBlur={() => setErrors({ ...errors, email: validateEmail(email) })}
              className={errors.email ? "error-input" : ""}
              required
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="register-phone">Phone Number <span className="required-asterisk">*</span></label>
            <input
              id="register-phone"
              type="tel"
              placeholder="Enter your phone number (10-15 digits)"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                if (errors.phone) {
                  setErrors({ ...errors, phone: validatePhone(e.target.value) });
                }
              }}
              onBlur={() => setErrors({ ...errors, phone: validatePhone(phone) })}
              className={errors.phone ? "error-input" : ""}
              required
            />
            {errors.phone && <span className="error-message">{errors.phone}</span>}
          </div>

          <div className="form-group password-group">
            <label htmlFor="register-password">Password <span className="required-asterisk">*</span></label>
            <input
              id="register-password"
              type={showPassword ? "text" : "password"}
              placeholder="Enter password (min 6 characters)"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errors.password) {
                  setErrors({ ...errors, password: validatePassword(e.target.value) });
                }
                if (errors.confirmPassword && e.target.value !== confirmPassword) {
                  setErrors({ ...errors, confirmPassword: "Passwords do not match" });
                } else if (errors.confirmPassword && e.target.value === confirmPassword) {
                  setErrors({ ...errors, confirmPassword: "" });
                }
              }}
              onBlur={() => setErrors({ ...errors, password: validatePassword(password) })}
              className={errors.password ? "error-input" : ""}
              required
              minLength={6}
            />
            <span
              className="toggle-password"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
            {errors.password && <span className="error-message">{errors.password}</span>}
          </div>

          <div className="form-group password-group">
            <label htmlFor="register-confirm-password">Confirm Password <span className="required-asterisk">*</span></label>
            <input
              id="register-confirm-password"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Re-enter your password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (e.target.value !== password) {
                  setErrors({ ...errors, confirmPassword: "Passwords do not match" });
                } else {
                  setErrors({ ...errors, confirmPassword: "" });
                }
              }}
              onBlur={() => {
                if (confirmPassword !== password) {
                  setErrors({ ...errors, confirmPassword: "Passwords do not match" });
                }
              }}
              className={errors.confirmPassword ? "error-input" : ""}
              required
              minLength={6}
            />
            <span
              className="toggle-password"
              onClick={() =>
                setShowConfirmPassword(!showConfirmPassword)
              }
            >
              {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
            {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
          </div>

          <button type="submit" className="btn" disabled={loading}>
            {loading ? "Registering..." : "Register"}
          </button>
        </form>
      </div>

      {/* Existing Customer Section */}
      <div className="login-right">
        <h2>Already a Customer?</h2>
        <p>
          If you already have an account with us, please log in to enjoy your
          personalized experience and access exclusive features.
        </p>
        <button className="btn" onClick={() => navigate("/login")}>
          Login
        </button>
      </div>

      {/* Verification Email Modal */}
      {showVerificationModal && (
        <div className="verification-modal-overlay" onClick={() => setShowVerificationModal(false)}>
          <div className="verification-modal" onClick={(e) => e.stopPropagation()}>
            <div className="verification-modal-content">
              <div className="verification-icon">📧</div>
              <h2>Check Your Email!</h2>
              <p>
                We've sent a verification email to <strong>{registeredEmail}</strong>
              </p>
              <p>
                Please click on the verification link in the email to activate your account.
              </p>
              <div className="verification-actions">
                <button 
                  className="btn btn-primary" 
                  onClick={() => {
                    setShowVerificationModal(false);
                    navigate("/login");
                  }}
                >
                  Go to Login
                </button>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setShowVerificationModal(false)}
                >
                  Close
                </button>
              </div>
              <p className="verification-note">
                Didn't receive the email? Check your spam folder or contact support.
              </p>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default RegisterPage;
